import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brandsApi } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

function BrandsPage() {
  const queryClient = useQueryClient();
  const [editingBrand, setEditingBrand] = useState(null);
  const [nameVariants, setNameVariants] = useState('');
  const [skipPriceEditing, setSkipPriceEditing] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const { data: brands, isLoading, error } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await brandsApi.getAll();
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => brandsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['brands']);
      setEditingBrand(null);
      setNameVariants('');
      toast.success('Бренд обновлён');
    },
    onError: () => {
      toast.error('Ошибка обновления бренда');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => brandsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['brands']);
      toast.success('Бренд удалён');
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Ошибка удаления бренда';
      toast.error(message);
    },
  });

  const handleEdit = (brand) => {
    setEditingBrand(brand.id);
    setNameVariants(brand.name_variants ? brand.name_variants.join('\n') : '');
  };

  const handleSave = (brandId) => {
    const variants = nameVariants
      .split('\n')
      .map(v => v.trim())
      .filter(v => v.length > 0);
    
    updateMutation.mutate({
      id: brandId,
      data: { name_variants: variants }
    });
  };

  const handleCancel = () => {
    setEditingBrand(null);
    setNameVariants('');
  };

  const handleDelete = (brand) => {
    if (brand.product_count > 0) {
      if (!window.confirm(`У бренда "${brand.name}" есть ${brand.product_count} товаров. Удалить бренд и все его товары?`)) {
        return;
      }
    } else {
      if (!window.confirm(`Удалить бренд "${brand.name}"?`)) {
        return;
      }
    }
    deleteMutation.mutate(brand.id);
  };

  const handleToggleSkipPrice = (brand) => {
    updateMutation.mutate({
      id: brand.id,
      data: { skip_price: !brand.skip_price }
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} style={{ opacity: 0.3, marginLeft: '4px' }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} style={{ marginLeft: '4px' }} />
      : <ArrowDown size={14} style={{ marginLeft: '4px' }} />;
  };

  if (isLoading) return <div className="loading">Загрузка брендов...</div>;
  if (error) return <div className="error">Ошибка: {error.message}</div>;

  // Фильтруем бренды по поисковому запросу
  const filteredBrands = brands?.filter(brand => 
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Сортируем бренды
  const sortedBrands = filteredBrands?.slice().sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    // Для дат
    if (key === 'created_at') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    // Для чисел
    else if (key === 'id' || key === 'product_count') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    }
    // Для строк
    else {
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="brands-page">
      <h1>Бренды</h1>
      <p className="info">
        {searchQuery ? (
          <>Найдено: <strong>{filteredBrands?.length || 0}</strong> из {brands?.length || 0}</>
        ) : (
          <>Всего брендов: <strong>{brands?.length || 0}</strong></>
        )}
      </p>
      
      {/* Поиск */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          className="search-input"
          placeholder="Поиск бренда..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              marginLeft: '8px',
              padding: '10px 16px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Сбросить
          </button>
        )}
      </div>
      
      <div className="table-container">
        <table className="brands-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                ID {getSortIcon('id')}
              </th>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                Название {getSortIcon('name')}
              </th>
              <th>GUID 1C</th>
              <th>Варианты написания</th>
              <th onClick={() => handleSort('product_count')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                Товаров {getSortIcon('product_count')}
              </th>
              <th style={{ textAlign: 'center' }}>Не отправлять цены</th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                Создан {getSortIcon('created_at')}
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedBrands?.map((brand) => (
              <tr key={brand.id}>
                <td>{brand.id}</td>
                <td><strong>{brand.name}</strong></td>
                <td className="guid">{brand.guid_1c || '—'}</td>
                <td>
                  {editingBrand === brand.id ? (
                    <textarea
                      className="variants-input"
                      value={nameVariants}
                      onChange={(e) => setNameVariants(e.target.value)}
                      placeholder="Введите варианты написания (по одному на строку)"
                      rows={5}
                    />
                  ) : (
                    <div className="variants-list">
                      {brand.name_variants && brand.name_variants.length > 0 ? (
                        brand.name_variants.map((variant, idx) => (
                          <span key={idx} className="variant-badge">{variant}</span>
                        ))
                      ) : (
                        <span className="no-variants">Нет вариантов</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="center">{brand.product_count || 0}</td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!brand.skip_price}
                    onChange={() => handleToggleSkipPrice(brand)}
                    title="Не отправлять цены на сайт"
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                </td>
                <td>
                  {brand.created_at ? new Date(brand.created_at).toLocaleString('ru-RU', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '—'}
                </td>
                <td>
                  {editingBrand === brand.id ? (
                    <div className="button-group">
                      <button
                        className="btn btn-success"
                        onClick={() => handleSave(brand.id)}
                        disabled={updateMutation.isLoading}
                      >
                        {updateMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={updateMutation.isLoading}
                      >
                        Отмена
                      </button>
                    </div>
                  ) : (
                    <div className="button-group">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(brand)}
                      >
                        Редактировать
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(brand)}
                        disabled={deleteMutation.isLoading}
                        style={{ marginLeft: '8px' }}
                      >
                        Удалить
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredBrands?.length === 0 && (
        <div className="empty-state">
          <p>{searchQuery ? 'Ничего не найдено. Попробуйте другой запрос.' : 'Брендов пока нет. Импортируйте товары из 1С.'}</p>
        </div>
      )}
    </div>
  );
}

export default BrandsPage;
